import { useEffect, useState } from "react";
import { Loader2, Repeat2, CalendarMinus } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtShift } from "./shiftFormat";

const NONE = "__none__";

// v2.8+E345 · ask to swap (with a named coworker) or drop one of your shifts.
export const ShiftRequestDialog = ({ shift, open, onOpenChange, onDone }) => {
  const [type, setType] = useState("swap");
  const [coworkers, setCoworkers] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [theirShifts, setTheirShifts] = useState([]);
  const [tradeId, setTradeId] = useState(NONE);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType("swap"); setTargetId(""); setTradeId(NONE); setReason(""); setTheirShifts([]);
    adminApi.get("/admin/shift-requests/coworkers").then(({ data }) => setCoworkers(data?.coworkers || [])).catch(() => setCoworkers([]));
  }, [open]);

  useEffect(() => {
    setTradeId(NONE);
    if (!targetId) { setTheirShifts([]); return; }
    adminApi.get(`/admin/shift-requests/coworker-shifts/${targetId}`)
      .then(({ data }) => setTheirShifts(data?.shifts || [])).catch(() => setTheirShifts([]));
  }, [targetId]);

  const submit = async () => {
    if (type === "swap" && !targetId) { toast.error("Pick a coworker"); return; }
    setBusy(true);
    try {
      await adminApi.post("/admin/shift-requests", {
        shift_id: shift.id, type,
        target_id: type === "swap" ? targetId : null,
        trade_shift_id: type === "swap" && tradeId !== NONE ? tradeId : null,
        reason: reason || null,
      });
      toast.success(type === "swap" ? "Swap request sent to your coworker" : "Drop request sent to supervisors");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  const TypeBtn = ({ value, Icon, label, testid }) => (
    <button
      type="button" onClick={() => setType(value)} data-testid={testid}
      className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${type === value
        ? "border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : "border-zinc-200 dark:border-zinc-800 text-zinc-500 hover:border-zinc-400"}`}
    >
      <Icon className="h-4 w-4"/>{label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shift-request-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change this shift</DialogTitle>
          <DialogDescription>{shift ? fmtShift(shift) : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <TypeBtn value="swap" Icon={Repeat2} label="Swap with coworker" testid="shift-request-type-swap"/>
            <TypeBtn value="drop" Icon={CalendarMinus} label="Drop shift" testid="shift-request-type-drop"/>
          </div>
          {type === "swap" && (
            <>
              <div>
                <Label className="text-xs">Coworker</Label>
                <Select value={targetId} onValueChange={setTargetId}>
                  <SelectTrigger data-testid="shift-request-coworker" className="mt-1"><SelectValue placeholder="Who should take it?"/></SelectTrigger>
                  <SelectContent>
                    {coworkers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`shift-request-coworker-opt-${c.id}`}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {targetId && (
                <div>
                  <Label className="text-xs">Take one of their shifts in return <span className="text-zinc-400">(optional)</span></Label>
                  <Select value={tradeId} onValueChange={setTradeId}>
                    <SelectTrigger data-testid="shift-request-trade" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Nothing — they just take my shift</SelectItem>
                      {theirShifts.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`shift-request-trade-opt-${s.id}`}>{fmtShift(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <p className="text-[11px] text-zinc-500">Your coworker accepts first, then a supervisor approves.</p>
            </>
          )}
          {type === "drop" && <p className="text-[11px] text-zinc-500">Once a supervisor approves, the shift goes on the open-shift board for anyone to claim.</p>}
          <div>
            <Label className="text-xs">Reason <span className="text-zinc-400">(optional)</span></Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} data-testid="shift-request-reason" className="mt-1"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="shift-request-submit" className="bg-amber-500 hover:bg-amber-600 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : null}Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftRequestDialog;
